import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Clicksign from "../../SignContract/embedded";
import DashboardModal from "../components/DashboardModal";
import {
  CLICKSIGN_CONTAINER_ID,
  CLICKSIGN_WIDGET_ENDPOINT,
  PENDING_SIGNATURES_REFRESH_DELAY_MS,
} from "../constants/clickSign";
import { fetchSignerKey } from "../services/ownerPatrimonyService";
import { IPendingSignatureItem } from "../types/ownerDashboard.types";

interface ClickSignWidget {
  endpoint: string;
  origin: string;
  mount: (containerId: string) => void;
  unmount: () => boolean;
  on: (event: string, callback: (data?: unknown) => void) => void;
}

interface SignContractModalProps {
  item: IPendingSignatureItem;
  signerKeyEndpoint: string;
  onClose: () => void;
  onSigned: () => Promise<void>;
}

function resolveErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;

    if (typeof message === "string" && message.trim()) {
      return message;
    }

    if (error.response?.status === 403) {
      return "Você não tem permissão para assinar este contrato.";
    }

    if (error.response?.status === 404) {
      return "Chave de assinatura não encontrada ou contrato já assinado.";
    }
  }

  return "Não foi possível carregar a assinatura. Tente novamente mais tarde.";
}

async function resolveSignerKey(
  item: IPendingSignatureItem,
  signerKeyEndpoint: string
): Promise<string> {
  if (item.signer_key) {
    return item.signer_key;
  }

  if (item.request_signature_key) {
    return item.request_signature_key;
  }

  if (item.rent_process_id == null) {
    throw new Error("Signer key requires rent_process_id or signer_key");
  }

  return fetchSignerKey(item.rent_process_id, signerKeyEndpoint);
}

export default function SignContractModal({
  item,
  signerKeyEndpoint,
  onClose,
  onSigned,
}: SignContractModalProps) {
  const widgetRef = useRef<ClickSignWidget | null>(null);
  const refreshTimeoutRef = useRef<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const mountWidget = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const signerKey = await resolveSignerKey(item, signerKeyEndpoint);

        if (!isMounted) {
          return;
        }

        widgetRef.current?.unmount();

        const ClicksignWidget = Clicksign as unknown as new (signerKey: string) => ClickSignWidget;
        const widget = new ClicksignWidget(signerKey);
        widget.endpoint = CLICKSIGN_WIDGET_ENDPOINT;
        widget.origin = `${window.location.origin}/owner-dashboard`;
        widget.mount(CLICKSIGN_CONTAINER_ID);

        widget.on("resized", (height) => {
          const container = document.getElementById(CLICKSIGN_CONTAINER_ID);

          if (container && typeof height === "number") {
            container.style.height = `${height}px`;
          }
        });

        widget.on("signed", () => {
          widget.unmount();
          widgetRef.current = null;
          onClose();

          refreshTimeoutRef.current = window.setTimeout(() => {
            void onSigned();
          }, PENDING_SIGNATURES_REFRESH_DELAY_MS);
        });

        widgetRef.current = widget;
        setIsLoading(false);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(resolveErrorMessage(error));
        setIsLoading(false);
      }
    };

    void mountWidget();

    return () => {
      isMounted = false;

      if (refreshTimeoutRef.current != null) {
        window.clearTimeout(refreshTimeoutRef.current);
      }

      widgetRef.current?.unmount();
      widgetRef.current = null;
    };
  }, [item, onClose, onSigned, signerKeyEndpoint]);

  return (
    <DashboardModal title={`Assinar — ${item.title}`} onClose={onClose} maxWidth="xl">
      {errorMessage ? (
        <div className="space-y-4">
          <p className="text-sm text-[#f85149]">{errorMessage}</p>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-white/[0.12] px-4 py-2 text-xs font-semibold text-[#f0f6fc] transition-colors hover:bg-white/[0.06]"
          >
            Fechar
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {item.tenant_name && (
            <p className="text-xs text-[#8b949e] sm:text-sm">
              Inquilino: <span className="font-semibold text-[#f0f6fc]">{item.tenant_name}</span>
            </p>
          )}
          {isLoading && (
            <p className="text-xs text-[#8b949e]">Carregando documento para assinatura...</p>
          )}
          <div
            id={CLICKSIGN_CONTAINER_ID}
            className="min-h-[600px] w-full overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0d1117]"
          />
        </div>
      )}
    </DashboardModal>
  );
}
